import { getSession } from 'next-auth/react';
import { logger } from '@/lib/logger';
import { tenantStore } from '@/lib/tenantStore';
import { HTTP_STATUS } from '@/constants/status';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  totalCount?: number;
  message?: string;
  error?: string;
}

export interface ApiConfig {
  baseUrl: string;
}

let globalApiConfig: ApiConfig | null = null;

export function setApiConfig(config: ApiConfig) {
  globalApiConfig = config;
}

function getApiConfig(): ApiConfig {
  if (globalApiConfig) {
    return globalApiConfig;
  }

  const baseUrl = process.env.API_URL;

  if (!baseUrl) {
    logger.error('API configuration not initialized and environment variables not available', 'ApiClient', {
      hasGlobalConfig: !!globalApiConfig,
      hasBaseUrl: !!baseUrl,
      isServer: typeof window === 'undefined',
    });
    throw new Error('Missing required environment variable: API_URL');
  }

  return { baseUrl };
}

export interface ApiFetchOptions extends RequestInit {
  tenant?: string;
  raw?: boolean;
}

async function fetchWithAuth<T>(endpoint: string, options?: ApiFetchOptions): Promise<T> {
  const { baseUrl } = getApiConfig();
  const url = `${baseUrl}${endpoint}`;
  const session = await getSession();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const tenant = options?.tenant || tenantStore.getTenant();
  if (tenant) {
    headers['tenant'] = tenant;
  }

  if (options?.headers) {
    const existingHeaders = new Headers(options.headers);
    existingHeaders.forEach((value, key) => {
      headers[key] = value;
    });
  }

  if (session?.accessToken) {
    headers['Authorization'] = `Bearer ${session.accessToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    signal: options?.signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    const sanitizedUrl = url.split('?')[0];

    logger.error('API request failed', 'ApiClient', {
      status: response.status,
      statusText: response.statusText,
      endpoint: sanitizedUrl,
      errorPreview: errorText.substring(0, 200),
    });

    let errorMessage = `API request failed: ${response.statusText}`;

    // Check for scenario view creation errors first (before other status codes)
    if (errorText) {
      try {
        const parsed = JSON.parse(errorText);
        const errorStr = parsed.message || parsed.error || '';

        // Detect BigQuery "table not found" or explicit view creation message
        if (
          errorStr.includes('Not found: Table') ||
          errorStr.includes('views are still being created') ||
          errorStr.includes('Scenario views are')
        ) {
          errorMessage =
            'Scenario views are still being created. This typically takes 5-10 minutes.\n\n' +
            'Please wait a few minutes and try again.';
          throw new Error(errorMessage);
        }

        errorMessage = errorStr || errorMessage;
      } catch (parseError) {
        // If it's our custom error, rethrow it
        if (parseError instanceof Error && parseError.message.includes('views are still being created')) {
          throw parseError;
        }
        // Otherwise, continue with default error handling
        if (errorText.length < 100) {
          errorMessage = errorText;
        }
      }
    }

    if (response.status === HTTP_STATUS.UNAUTHENTICATED) {
      errorMessage = 'Authentication required. Please log in again.';
    } else if (response.status === HTTP_STATUS.FORBIDDEN) {
      errorMessage = 'Access denied. You do not have permission to access this resource.';
    } else if (response.status === HTTP_STATUS.NOT_FOUND) {
      errorMessage = 'Resource not found. The requested data may have been moved or deleted.';
    } else if (response.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR) {
      errorMessage = 'Server error. Please try again later or contact support if the problem persists.';
    }

    throw new Error(errorMessage);
  }

  if (response.status === HTTP_STATUS.NO_CONTENT) {
    return null as T;
  }

  return response.json();
}

export async function apiFetch<T>(endpoint: string, options?: ApiFetchOptions): Promise<T> {
  const json = await fetchWithAuth<any>(endpoint, options);

  if (json && typeof json === 'object' && 'data' in json) {
    if ('totalCount' in json) {
      return { data: json.data, totalCount: json.totalCount } as T;
    }
    return json.data;
  }
  return json;
}

export async function apiFetchRaw<T>(endpoint: string, options?: ApiFetchOptions): Promise<T> {
  return fetchWithAuth<T>(endpoint, options);
}
